import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ClauseLibraryViewType } from '~/enums/ClauseLibraryViewType';

import { State } from './types';

export const initialState: State = {
  clauseSummaries: [],
  view: ClauseLibraryViewType.MainPage,
  editMode: false,
  selectedClause: undefined,
  filters: null,
  clauseForm: {
    name: '',
    guidanceNotes: '',
    title: '',
    errors: [],
    validationErrors: [],
  },
  variationForm: {
    name: '',
    guidanceNotes: '',
    clauseText: '',
    title: '',
    validationErrors: [],
  },
};

export default createSlice({
  name: 'clauseLibrary',
  initialState,
  selectors: {
    selectClauseSummaries: (state: State) => state.clauseSummaries,
    selectClauseForm: (state: State) => state.clauseForm,
    selectSelectedClause: (state: State) => state.selectedClause,
    selectFilters: (state: State) => state.filters,
    selectVariationForm: (state: State) => state.variationForm,
    selectView: (state: State) => state.view,
    selectEditMode: (state: State) => state.editMode,
  },
  reducers: {
    resetClauseLibraryExceptSelectedClause: (state: State) => {
      return {
        ...initialState,
        selectedClause: state.selectedClause,
        clauseSummaries: state.clauseSummaries,
      };
    },
    cleanupClauseSummaries: (state: State) => {
      return { ...state, clauseSummaries: [] };
    },
    setEditMode: (state: State, action: PayloadAction<State['editMode']>) => {
      state.editMode = action.payload;
    },
    setClauseForm: (
      state: State,
      action: PayloadAction<State['clauseForm']>,
    ) => {
      state.clauseForm = action.payload;
    },
    setClauseGuidanceNotes: (
      state: State,
      action: PayloadAction<State['clauseForm']['guidanceNotes']>,
    ) => {
      state.clauseForm = { ...state.clauseForm, guidanceNotes: action.payload };
    },
    setClauseSummaries: (
      state: State,
      action: PayloadAction<State['clauseSummaries']>,
    ) => {
      state.clauseSummaries = action.payload;
    },
    setVariationForm: (
      state: State,
      action: PayloadAction<State['variationForm']>,
    ) => {
      state.variationForm = action.payload;
    },
    setVariationGuidanceNotes: (
      state: State,
      action: PayloadAction<State['variationForm']['guidanceNotes']>,
    ) => {
      state.variationForm = {
        ...state.variationForm,
        guidanceNotes: action.payload,
      };
    },
    setVariationClauseText: (
      state: State,
      action: PayloadAction<State['variationForm']['clauseText']>,
    ) => {
      state.variationForm = {
        ...state.variationForm,
        clauseText: action.payload,
      };
    },
    // validation errors returned by component, errors returned by backend
    resetClauseFormGuidanceError: (state: State) => {
      state.clauseForm = {
        ...state.clauseForm,
        errors: state.clauseForm.errors?.filter(
          (error) =>
            error.source?.pointer && error.source.pointer !== '/guidance',
        ),
      };
    },
    resetVariationEditorsValidationErrors: (state: State) => {
      state.variationForm = {
        ...state.variationForm,
        validationErrors: state.variationForm.validationErrors?.filter(
          (error) => error !== 'text missing',
        ),
      };
    },
    resetVariationGuidanceError: (state: State) => {
      state.variationForm = {
        ...state.variationForm,
        errors: state.variationForm.errors?.filter(
          (error) =>
            error.source?.pointer && error.source.pointer !== '/guidance',
        ),
      };
    },
    resetVariationTextError: (state: State) => {
      state.variationForm = {
        ...state.variationForm,
        errors: state.variationForm.errors?.filter(
          (error) => error.source?.pointer && error.source.pointer !== '/text',
        ),
      };
    },
    setView: (state: State, action: PayloadAction<State['view']>) => {
      state.view = action.payload;
    },
    setSelectedClause: (
      state: State,
      action: PayloadAction<State['selectedClause']>,
    ) => {
      state.selectedClause = action.payload;
    },
    setFilter: (state: State, action: PayloadAction<State['filters']>) => {
      state.filters = action.payload;
    },
  },
});
